import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, computed, makeAutoObservable, observable} from "mobx";
import {RequestState, TeamRegion} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IFilter, IPlayerPoolStore} from "data/stores/player_pool/player_pool.store";
import type {IPlayer, IPlayersStore} from "data/stores/players/players.store";
import {ChangeEvent, SyntheticEvent} from "react";
import {chain, includes} from "lodash";
import type {ITeamStore} from "data/stores/team/team.store";

interface IFilterForm extends HTMLFormElement {
	search: HTMLInputElement;
}

export interface IPoolController extends ViewController {
	get i18n(): ILocalizationStore;

	get filter(): IFilter;

	get playersList(): IPlayer[];
	get allLineup(): number[];
	get isCurrentLineupFull(): boolean;
	get selectedRegion(): TeamRegion;

	handleFormOnChange: (event: ChangeEvent<IFilterForm>) => void;
	handleFormSubmit: (event: SyntheticEvent<IFilterForm>) => void;
	onAddPlayer: (playerId: number, isMobile: boolean) => void;
	onRemovePlayer: (playerId: number, isMobile: boolean) => void;
}

@injectable()
export class PoolController implements IPoolController {
	@observable _requestState: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.PlayerPoolStore) private _poolStore: IPlayerPoolStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get filter() {
		return this._poolStore.filter;
	}

	@computed get playersList() {
		const playersList =
			this._teamStore.selectedRegion === TeamRegion.Europe
				? this._playerStore.getEuropePlayers
				: this._playerStore.getUsaPlayers;

		return chain(playersList)
			.filter((player) => !includes(this._teamStore.allLineup, player.id))
			.filter(this.isListed)
			.orderBy("worldRanking", "asc")
			.value();
	}

	get allLineup() {
		return this._teamStore.allLineup;
	}

	get isCurrentLineupFull() {
		return this._teamStore.isCurrentLineupFull;
	}

	get selectedRegion() {
		return this._teamStore.selectedRegion;
	}

	isListed = (player: IPlayer) => {
		const {search} = this.filter;
		return !search || player.fullName.toLowerCase().includes(search.toLowerCase());
	};

	onAddPlayer = (playerId: number, isMobile: boolean) => {
		this._teamStore.addPlayer(playerId);
		if (isMobile) {
			this._poolStore.closePoolModal();
		}
	};

	onRemovePlayer = (playerId: number, isMobile: boolean) => {
		this._teamStore.removePlayer(playerId);
		if (isMobile) {
			this._poolStore.closePoolModal();
		}
	};

	@action handleFormOnChange = (event: ChangeEvent<IFilterForm>) => {
		const {search} = event.currentTarget;
		this.filter.search = search.value;
	};

	@action handleFormSubmit = (event: SyntheticEvent<IFilterForm>) => {
		event.preventDefault();
	};

	dispose() {
		return;
	}

	init(param: void) {
		return;
	}
}
